<template>
    <div>
        <v-dialog v-model="showWelcomeDialog" contained max-width="500" :persistent="!dataLoaded" class="custom-dialog">
            <v-card rounded="lg">
                <template #text>
                    <div class="text-center pt-4">

                        <v-progress-circular v-if="!projectName || loadingData"
                                             indeterminate
                                             color="primary"
                                             size="x-large"
                                             class="mb-4" />
                        <v-avatar v-else
                                  class="mb-4 cursor-pointer"
                                  color="success"
                                  size="x-large"
                                  variant="tonal"
                                  icon="mdi-check"
                                  @dblclick="copyQueryString"></v-avatar>

                        <div class="font-weight-bold mb-2">
                            {{ $t('welcomeMessage', { projectName: projectName }) }}
                        </div>

                        <div class="text-body-2 text-medium-emphasis mb-6"></div>

                        <!-- Overall Progress -->
                        <div v-if="!projectName || loadingData" class="px-4">
                            <v-progress-linear :model-value="progressValue"
                                               color="primary"
                                               height="6"
                                               class="mb-2"
                                               rounded />
                            <div class="text-caption text-center mb-4">
                                {{ completedTables }} / {{ totalApiRequests }} {{ $t('loaded') }}
                            </div>

                            <!-- Current Table Loading Progress -->
                            <div v-if="currentTableLoading" class="mb-4">
                                <v-divider class="mb-3" />
                                <div class="text-body-2 font-weight-medium mb-2">
                                    <v-icon size="small" class="mr-1">mdi-table</v-icon>
                                    {{ currentTableLoading.displayName }}
                                </div>

                                <!-- Paging Progress for Tables with Multiple Pages -->
                                <div v-if="currentTableLoading.status === 'loading' && currentTableLoading.currentPage" class="mb-2">
                                    <div class="d-flex align-center justify-space-between mb-1">
                                        <span class="text-caption">{{ $t('loadingPage') }} {{ currentTableLoading.currentPage }}</span>
                                        <span class="text-caption text-grey">{{ formatNumber(currentTableLoading.rowsLoaded) }} {{ $t('rowsLoaded') }}</span>
                                    </div>
                                    <v-progress-linear indeterminate
                                                       color="secondary"
                                                       height="3"
                                                       rounded
                                                       class="mb-1" />
                                    <div class="text-caption text-center text-grey">
                                        {{ $t('paginationInProgress') }}
                                    </div>
                                </div>

                                <!-- Simple Progress for Non-Paged Tables -->
                                <div v-else-if="currentTableLoading.status === 'loading'" class="mb-2">
                                    <v-progress-linear indeterminate
                                                       color="secondary"
                                                       height="3"
                                                       rounded />
                                </div>
                            </div>

                            <!-- Table Status List -->
                            <div v-if="tableStatusList.length > 0" class="table-status-list">
                                <v-divider class="mb-2" />
                                <div class="text-caption" style="max-height: 150px; overflow-y: auto;">
                                    <div v-for="table in tableStatusList"
                                         :key="table.name"
                                         class="d-flex align-center justify-space-between py-1">
                                        <span class="text-truncate" style="max-width: 200px;">{{ table.displayName }}</span>
                                        <div class="d-flex align-center">
                                            <v-icon :color="getStatusColor(table.status)"
                                                    size="x-small"
                                                    class="mr-1">
                                                {{ getStatusIcon(table.status) }}
                                            </v-icon>
                                            <span class="text-caption text-grey ml-1">
                                                <template v-if="table.status === 'completed'">
                                                    <span v-if="table.rowsLoaded">{{ formatNumber(table.rowsLoaded) }}</span>
                                                    <span v-if="table.totalPages > 1" class="ml-1">({{ table.totalPages }}p)</span>
                                                </template>
                                                <template v-else-if="table.status === 'loading' && table.currentPage">
                                                    p{{ table.currentPage }}
                                                </template>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <v-btn v-if="projectName"
                               block
                               class="text-none"
                               color="primary"
                               :disabled="loadingData || !isDuckDBInitialized"
                               variant="flat"
                               @click="loadDashboard">
                            {{ $t('loadDashboard') }}
                        </v-btn>
                    </div>
                </template>
            </v-card>
        </v-dialog>

        <!-- Copy Dialog for Manual Copy -->
        <v-dialog v-model="showCopyDialog" max-width="400">
            <v-card>
                <v-card-title>{{ $t('copyQueryStringTitle') }}</v-card-title>
                <v-card-text>
                    <v-text-field ref="copyInput"
                                  :value="queryString"
                                  readonly
                                  label="Query String"
                                  variant="outlined"></v-text-field>
                    <p class="text-caption">{{ $t('manualCopyInstruction') }}</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="manualCopy">
                        {{ $t('copy') }}
                    </v-btn>
                    <v-btn color="grey" text @click="showCopyDialog = false">
                        {{ $t('close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Toast Message -->
        <v-snackbar v-model="showToast"
                    :timeout="2000"
                    color="success"
                    location="top">
            {{ $t('devModeActivated') }}
        </v-snackbar>

        <!-- Error Toast for Copy Failure -->
        <v-snackbar v-model="showErrorToast"
                    :timeout="3000"
                    color="error"
                    location="top">
            {{ $t('copyFailed') }}
        </v-snackbar>
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useAnalytics } from '@/utils/useAnalytics'
import { useI18n } from 'vue-i18n'

const store = useStore();
const { trackEvent } = useAnalytics()
const { t } = useI18n()

const showWelcomeDialog = computed(() => store.getters.getShowWelcomeDialog);
const isDuckDBInitialized = computed(() => store.getters.isDuckDBInitialized);
const showToast = ref(false);
const showErrorToast = ref(false);
const showCopyDialog = ref(false);
const copyInput = ref(null);

const projectName = computed(() => store.getters.getProjectName);
const loadingData = ref(false);
const dataLoaded = computed(() => store.getters.getDataLoaded);
const queryString = computed(() => window.location.search);

// Props for progress tracking
const props = defineProps({
  apiProgress: {
    type: Number,
    required: true
  },
  totalApiRequests: {
    type: Number,
    required: true
  },
  tableProgress: {
    type: Object,
    default: () => ({})
  }
});

// Compute the progress percentage
const progressValue = computed(() => {
  return (props.apiProgress / props.totalApiRequests) * 100;
});

// Track table loading status
const tableStatusList = ref([]);
const completedTables = computed(() =>
  tableStatusList.value.filter(t => t.status === 'completed').length
);

const currentTableLoading = computed(() =>
  tableStatusList.value.find(t => t.status === 'loading')
);

// Watch for table progress updates
watch(() => props.tableProgress, (progress) => {
  if (!progress || !progress.tableName) return;

  let table = tableStatusList.value.find(t => t.name === progress.tableName);
  if (!table) {
    table = {
      name: progress.tableName,
      displayName: getTableDisplayName(progress.tableName),
      status: 'pending',
      rowsLoaded: 0,
      currentPage: null,
      totalPages: null
    };
    tableStatusList.value.push(table);
  }

  // Update table status
  table.status = progress.status;
  if (progress.rowsLoaded !== undefined) table.rowsLoaded = progress.rowsLoaded;
  if (progress.currentPage !== undefined) table.currentPage = progress.currentPage;
  if (progress.totalPages !== undefined) table.totalPages = progress.totalPages;
  if (progress.timeMs !== undefined) table.timeMs = progress.timeMs;
}, { deep: true });

// Helper functions
function getTableDisplayName(tableName) {
  const nameMap = {
    'assets': t('table_assets'),
    'status_history': t('table_statusHistory'),
    'item_templates': t('table_itemTemplates'),
    'categories': t('table_categories'),
    'status_templates': t('table_statusTemplates'),
    'drawings': t('table_drawings'),
    'locations': t('table_locations'),
    'teams': t('table_teams'),
    'team_users': t('table_teamUsers'),
    'users': t('table_users'),
    'current_user': t('table_currentUser'),
    'permission_rules': t('table_permissionRules'),
    'company': t('table_company')
  };
  return nameMap[tableName] || tableName;
}

function getStatusIcon(status) {
  switch (status) {
    case 'completed': return 'mdi-check-circle';
    case 'loading': return 'mdi-loading mdi-spin';
    case 'error': return 'mdi-alert-circle';
    default: return 'mdi-circle-outline';
  }
}

function getStatusColor(status) {
  switch (status) {
    case 'completed': return 'success';
    case 'loading': return 'primary';
    case 'error': return 'error';
    default: return 'grey';
  }
}

function formatNumber(num) {
  return new Intl.NumberFormat().format(num);
}

const copyQueryString = () => {
  const qs = queryString.value;

  const handleSuccess = () => {
    showToast.value = true;
    trackEvent('copyQueryString', {
      action_type: 'avatar_double_click',
      project_code: store.getters.getProjectCode,
      project_name: store.getters.getProjectName
    });
  };

  const handleFailure = (err) => {
    console.error('Failed to copy query string:', err);
    showCopyDialog.value = true;
  };

  if (navigator.clipboard) {
    navigator.clipboard.writeText(qs)
      .then(handleSuccess)
      .catch(handleFailure);
  } else {
    // Fallback for older browsers or restricted environments
    const textArea = document.createElement("textarea");
    textArea.value = qs;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        handleSuccess();
      } else {
        handleFailure(new Error('execCommand failed'));
      }
    } catch (err) {
      handleFailure(err);
    } finally {
      document.body.removeChild(textArea);
    }
  }
};

const manualCopy = () => {
  const input = copyInput.value.$el.querySelector('input');
  if (input) {
    input.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        showToast.value = true;
        showCopyDialog.value = false; // Close dialog on success
        trackEvent('copyQueryString', {
          action_type: 'manual_copy',
          project_code: store.getters.getProjectCode,
          project_name: store.getters.getProjectName
        });
      } else {
        showErrorToast.value = true;
      }
    } catch (err) {
      console.error('Manual copy failed:', err);
      showErrorToast.value = true;
    }
  }
};

// Auto-select text in copy dialog
watch(showCopyDialog, async (newVal) => {
  if (newVal) {
    await nextTick();
    const input = copyInput.value.$el.querySelector('input');
    if (input) {
      input.select();
    }
  }
});

const loadDashboard = async () => {
  trackEvent('loadDashboard', {
    action_type: 'button_click',
    project_code: store.getters.getProjectCode,
    project_name: store.getters.getProjectName
  })

  loadingData.value = true;
  await store.dispatch('updateLoadDashboard', true);
};

watch(dataLoaded, (newValue) => {
  if (newValue) {
    store.dispatch('updateShowWelcomeDialog', false);
  }
});

</script>

<style>
    .custom-dialog .v-overlay__content {
        top: 20vh !important;
    }

    .cursor-pointer {
        cursor: pointer;
    }

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }

        to {
            transform: rotate(360deg);
        }
    }

    .mdi-spin {
        animation: spin 1s linear infinite;
    }

    .table-status-list {
        font-size: 0.75rem;
    }
</style>